"use client"

import Link from "next/link"
import { cn } from "@/lib/utils"
import {
  BarChart3, Users, List, Mail, FileText, Archive, Settings, Sun,
  User, LogOut, Building2, Inbox,
  ChevronLeft, ChevronRight
} from "lucide-react"
import Image from "next/image"
import { usePathname, useRouter } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { logoutWithRouter } from '@/utils/logout';
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from '@/lib/api/apiFetch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth"
import { useUser } from "@/hooks/useUser"
import { SidebarSkeleton } from "./SidebarSkeleton"

const navigation = [
  // { name: "Analysis", href: "/analysis", icon: BarChart3, role: ["user", "orgAdmin"] },
  { name: "Analytics", href: "/analytics", icon: BarChart3, role: ["superAdmin"] },
  { name: "Contacts", href: "/contacts", icon: Users, role: ["user", "orgAdmin"] },
  { name: "Lists", href: "/lists", icon: List, role: ["user", "orgAdmin"] },
  { name: "Email Campaign", href: "/campaigns", icon: Mail, role: ["user", "orgAdmin"] },
  { name: "Templates", href: "/templates", icon: FileText, role: ["user", "orgAdmin"] },
  { name: "Archive", href: "/archive", icon: Archive, role: ["user", "orgAdmin"] },
  { name: "Organization", href: "/organization", icon: Building2, role: ["user", "orgAdmin", "superAdmin"] },
  { name: "Settings", href: "/settings", icon: Settings, role: ["user", "orgAdmin"] },
  { name: "Requests", href: "/requests", icon: Inbox, role: ["superAdmin"] },
]

interface SidebarProps {
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function Sidebar({ collapsed = false, onToggleCollapse }: SidebarProps) {
  const pathname = usePathname()
  const router = useRouter();
  const { toast } = useToast();
  const { logoutHandler } = useAuth();

  const { user, loading } = useUser()

  const roleBasedNavigation = navigation.filter((item) => {
    if (!user?.role) return false
    return item.role.includes(user?.role)
  })

  const handleLogout = async () => {
    try {
      // Call logout API
      await apiFetch('/api/auth/logout', {
        method: 'POST',
      });

      // Clear client-side data and redirect
      logoutWithRouter(router);
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Logout failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      router.push("/login");
    }
  };

  if (loading || !user?.role) {
    return <SidebarSkeleton collapsed={collapsed} />
  }

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-10 flex h-screen flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-[width] duration-200"
        ,
        collapsed ? "w-16" : "w-48"
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 border-b border-gray-200 dark:border-gray-800 cursor-pointer",
          collapsed ? "justify-center p-2" : "justify-center p-1"
        )}
        onClick={() => router.push("/contacts")}
      >
        {collapsed ? (
          <Image
            alt="logo"
            src="/images/fav_icon/Icom_Fav_icon.png"
            width={32}
            height={40}
            className="min-w-8 min-h-12 object-contain"
          />
        ) : (
          <>
            <Image
              alt="logo-light"
              src="/images/svg_logo/icomm_Logo_01.svg"
              width={100}
              height={50}
              className="block dark:hidden"
            />
            <Image
              alt="logo-dark"
              src="/images/svg_logo/icomm_Logo_04.svg"
              width={100}
              height={50}
              className="hidden dark:block"
            />
          </>
        )}

      </div>

      <nav className={cn("flex-1 space-y-1 py-4", collapsed ? "px-2" : "px-4")}>
        {roleBasedNavigation.map((item: any) => {
          const isActive = pathname ? pathname.startsWith(item.href) : false
          return (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                "flex items-center rounded-md text-xs font-medium transition-colors group",
                collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-gray-900 text-[#07beff] dark:bg-gray-700"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"

              )}
              title={collapsed ? item.name : undefined}
            >
              <item.icon
                className={`h-4 w-4 shrink-0 transition-colors duration-200 ${isActive
                  ? "text-[#07beff]"
                  : "text-gray-700 dark:text-gray-300 group-hover:text-[#07beff]"
                  }`}
              />
              {!collapsed && (
                <>
                  {item.name}
                  {item?.count && (
                    <span className="ml-auto text-xs bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 px-2 py-0.5 rounded-full"
                    >{item?.count}</span>
                  )}
                </>
              )}
            </Link>
          )
        })}
      </nav>

      <div className={cn("border-t space-y-2 relative", collapsed ? "p-2" : "p-4")}>
        <Link
          key={'account'}
          href={'/account'}
          className={cn(
            "group flex items-center rounded-md text-xs font-medium transition-colors",
            collapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2",
            pathname?.startsWith('/account')
              ? "bg-gray-900 text-[#07beff] dark:bg-gray-700"
              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          )}
          title={collapsed ? 'Account' : undefined}
        >
          <User
            className={cn(
              "h-4 w-4 shrink-0 transition-colors duration-200",
              pathname?.startsWith('/account') ? "text-[#07beff]" : "text-gray-700 dark:text-gray-300 group-hover:text-[#07beff]"
            )}
          />
          {!collapsed && (
            <>
              {'Account'}
            </>
          )}
        </Link>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50",
                collapsed ? "w-full justify-center px-0" : "w-full"
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed && "Logout"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
              <AlertDialogDescription>
                This action will end your current session and you will need to login again to access your account.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={logoutHandler}
                className="bg-red-600 hover:bg-red-700"
              >
                Logout
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Floating Collapse Toggle */}
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          className={`absolute -right-3  -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-[#07beff] text-white shadow-md transition-transform hover:scale-110 focus:outline-none ${collapsed ? "bottom-24" : "bottom-28"}`}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      )}
    </aside>
  )
}
